"use client";

import * as React from "react";
import { ACTIVE_ACCOUNTS, accountLabel, findAccount } from "../../data/accounts";
import { pickCreditorTemplate } from "../../data/postingTemplates";

type Item = { accountNo: string; memo: string; net: string };

type PreviewLine = { accountNo: string; memo?: string; debit: number; credit: number };

type PreviewEntry = {
  date: string;
  ref: string;
  vendor: string;
  text: string;
  vatRate: number;
  lines: PreviewLine[];
  totals: { net: number; vat: number; gross: number; debit: number; credit: number; diff: number };
};

const EMPTY_ENTRY: PreviewEntry = {
  date: "",
  ref: "",
  vendor: "",
  text: "",
  vatRate: 0,
  lines: [],
  totals: { net: 0, vat: 0, gross: 0, debit: 0, credit: 0, diff: 0 },
};

function parseMoney(v: string): number {
  const s = (v || "")
    .toString()
    .replace(/[^0-9,.\-]/g, "")
    .replace(",", ".");
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

export default function AutoCreditorPostModal({
  open,
  onClose,
  onCreated,
  companySlug,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  companySlug: string;
}) {
  const [date, setDate] = React.useState(new Date().toISOString().slice(0, 10));
  const [ref, setRef] = React.useState("");
  const [vendor, setVendor] = React.useState("");
  const [text, setText] = React.useState("");
  const [vatRate, setVatRate] = React.useState(0.081);
  const [apAccountNo, setApAccountNo] = React.useState("2000");
  const [inputVatAccountNo, setInputVatAccountNo] = React.useState("1170");
  const [entry, setEntry] = React.useState<PreviewEntry>(EMPTY_ENTRY);
  const [creating, setCreating] = React.useState(false);

  const [items, setItems] = React.useState<Item[]>([
    { accountNo: "", memo: "", net: "" },
  ]);

  React.useEffect(() => {
    if (!open) return;
    setDate(new Date().toISOString().slice(0, 10));
    setRef("");
    setVendor("");
    setText("");
    setVatRate(0.081);
    setApAccountNo("2000");
    setInputVatAccountNo("1170");
    setItems([{ accountNo: "", memo: "", net: "" }]);
    setEntry(EMPTY_ENTRY);
  }, [open]);

  React.useEffect(() => {
    const t = pickCreditorTemplate(vendor);
    if (!t) return;
    setVatRate(t.defaultVatRate);
    setItems((prev) => {
      if (prev.length === 0) return prev;
      const first = prev[0];
      if (first.accountNo) return prev;
      const next = [...prev];
      next[0] = { ...first, accountNo: t.defaultExpenseAccountNo };
      return next;
    });
  }, [vendor]);

  React.useEffect(() => {
    if (!open) return;
    const mappedItems = items
      .map((it) => ({
        accountNo: it.accountNo,
        memo: it.memo,
        net: parseMoney(it.net),
      }))
      .filter((it) => it.net > 0 || it.accountNo);

    const payload = {
      date,
      ref: ref || "AUTO",
      vendor: vendor || "Unbekannt",
      text: text || `Kreditor ${vendor || ""}`.trim(),
      vatRate,
      apAccountNo,
      inputVatAccountNo,
      items: mappedItems.length ? mappedItems : [],
    };

    const timer = window.setTimeout(async () => {
      try {
        const res = await fetch("/api/posting/creditor-preview", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (res.ok && data.ok) {
          setEntry(data as PreviewEntry);
        }
      } catch {
        /* preview optional */
      }
    }, 250);

    return () => window.clearTimeout(timer);
  }, [open, date, ref, vendor, text, vatRate, apAccountNo, inputVatAccountNo, items]);

  const itemErrors = React.useMemo(() => {
    return items.map((it) => {
      const net = parseMoney(it.net);
      const hasAmount = net > 0;
      const missingAccount = hasAmount && !it.accountNo;
      return { missingAccount };
    });
  }, [items]);

  const hasItemErrors = itemErrors.some((e) => e.missingAccount);
  const balanced = Math.abs(entry.totals.diff) < 0.01 && (entry.totals.debit + entry.totals.credit) > 0;
  const canCreate = balanced && !hasItemErrors && !!vendor && !creating;

  function addItem() {
    setItems((p) => [...p, { accountNo: "", memo: "", net: "" }]);
  }

  function removeItem(idx: number) {
    setItems((p) => (p.length <= 1 ? p : p.filter((_, i) => i !== idx)));
  }

  function updateItem(idx: number, patch: Partial<Item>) {
    setItems((p) => p.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }

  async function createProvisional() {
    if (!canCreate) return;
    setCreating(true);
    try {
      const res = await fetch("/api/journal", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          companySlug,
          date: entry.date,
          ref: entry.ref,
          text: `AUTO: ${entry.vendor} · ${entry.text}`,
          status: "PROVISORISCH",
          source: "AUTO_CREDITOR",
          lines: entry.lines.map((l) => ({
            accountNo: l.accountNo,
            memo: l.memo,
            debit: l.debit,
            credit: l.credit,
          })),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Buchung fehlgeschlagen");
      }
      onCreated();
      onClose();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Buchung fehlgeschlagen");
    } finally {
      setCreating(false);
    }
  }

  if (!open) return null;

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true">
      <div className="modal">
        <div className="modal-head">
          <div>
            <div className="modal-title">Kreditor Auto-Buchen</div>
            <div className="modal-sub">Vorlage → Multi-Buchung → MWST Split → PROVISORISCH</div>
          </div>
          <button className="btn btn-ghost" type="button" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          <div className="grid-2">
            <label className="field">
              <span>Datum</span>
              <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </label>

            <label className="field">
              <span>Referenz</span>
              <input className="input" placeholder="z.B. KR-10055" value={ref} onChange={(e) => setRef(e.target.value)} />
            </label>
          </div>

          <div className="grid-2">
            <label className="field">
              <span>Lieferant</span>
              <input className="input" placeholder="z.B. Swisscom" value={vendor} onChange={(e) => setVendor(e.target.value)} />
            </label>

            <label className="field">
              <span>MWST Satz</span>
              <select className="select" value={String(vatRate)} onChange={(e) => setVatRate(Number(e.target.value))}>
                <option value="0">0.0%</option>
                <option value="0.026">2.6%</option>
                <option value="0.038">3.8%</option>
                <option value="0.081">8.1%</option>
              </select>
            </label>
          </div>

          <label className="field">
            <span>Text</span>
            <input className="input" placeholder="z.B. Rechnung Februar" value={text} onChange={(e) => setText(e.target.value)} />
          </label>

          <div className="grid-2">
            <label className="field">
              <span>Gegenkonto (Kreditoren)</span>
              <select className="select" value={apAccountNo} onChange={(e) => setApAccountNo(e.target.value)}>
                {ACTIVE_ACCOUNTS.filter(a => a.type === "Liability").map(a => (
                  <option key={a.number} value={a.number}>{accountLabel(a)}</option>
                ))}
              </select>
            </label>

            <label className="field">
              <span>Vorsteuer-Konto</span>
              <select className="select" value={inputVatAccountNo} onChange={(e) => setInputVatAccountNo(e.target.value)}>
                {ACTIVE_ACCOUNTS.filter(a => a.type === "Asset").map(a => (
                  <option key={a.number} value={a.number}>{accountLabel(a)}</option>
                ))}
              </select>
            </label>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6, marginBottom: 10 }}>
            <div style={{ color: "var(--muted)", fontSize: 13 }}>
              Positionen (netto) – jede Zeile wird Soll; MWST & Kreditor werden automatisch erzeugt.
            </div>
            <button className="btn" type="button" onClick={addItem}>+ Position</button>
          </div>

          <div style={{ border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden", background: "var(--panel)" }}>
            <div style={{
              display: "grid",
              gridTemplateColumns: "1.4fr 1.2fr 0.6fr 60px",
              padding: "10px 12px",
              borderBottom: "1px solid var(--border)",
              fontWeight: 800
            }}>
              <div>Konto</div><div>Memo</div><div>Netto</div><div></div>
            </div>

            {items.map((it, idx) => {
              const warn = itemErrors[idx]?.missingAccount;
              return (
                <div key={idx} style={{
                  display: "grid",
                  gridTemplateColumns: "1.4fr 1.2fr 0.6fr 60px",
                  gap: 10,
                  alignItems: "center",
                  padding: "10px 12px",
                  borderBottom: idx === items.length - 1 ? "none" : "1px solid var(--border)",
                  background: warn ? "rgba(245,158,11,0.08)" : "transparent"
                }}>
                  <select className="select" value={it.accountNo} onChange={(e) => updateItem(idx, { accountNo: e.target.value })}>
                    <option value="">— Konto wählen —</option>
                    {ACTIVE_ACCOUNTS.filter(a => a.type === "Expense").map(a => (
                      <option key={a.number} value={a.number}>{accountLabel(a)}</option>
                    ))}
                  </select>

                  <input className="input" placeholder="optional" value={it.memo} onChange={(e) => updateItem(idx, { memo: e.target.value })} />

                  <input className="input" placeholder="0.00" value={it.net} onChange={(e) => updateItem(idx, { net: e.target.value })} />

                  <button className="btn btn-ghost" type="button" onClick={() => removeItem(idx)} title="Löschen">🗑️</button>
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div style={{ border: "1px solid var(--border)", borderRadius: 12, padding: 12, background: "rgba(0,0,0,0.02)" }}>
              <div style={{ fontWeight: 800, marginBottom: 6 }}>Totals</div>
              <div>Netto: <strong>CHF {entry.totals.net.toFixed(2)}</strong></div>
              <div>MWST: <strong>CHF {entry.totals.vat.toFixed(2)}</strong></div>
              <div>Brutto: <strong>CHF {entry.totals.gross.toFixed(2)}</strong></div>
              <div style={{ marginTop: 8 }}>
                Diff: <span className={balanced ? "pill pill-ok" : "pill pill-warn"}>{entry.totals.diff.toFixed(2)}</span>
              </div>
            </div>

            <div style={{ border: "1px solid var(--border)", borderRadius: 12, padding: 12, background: "var(--panel)" }}>
              <div style={{ fontWeight: 800, marginBottom: 6 }}>Buchungs-Vorschau</div>
              <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 8 }}>
                (Soll/Haben Zeilen)
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 0.6fr 0.6fr", gap: 8, fontSize: 13 }}>
                <div style={{ fontWeight: 800 }}>Konto</div>
                <div style={{ fontWeight: 800 }}>Soll</div>
                <div style={{ fontWeight: 800 }}>Haben</div>
                {entry.lines.map((l, i) => (
                  <React.Fragment key={i}>
                    <div>
                      {l.accountNo} · {findAccount(l.accountNo)?.name || ""}
                    </div>
                    <div>{l.debit ? l.debit.toFixed(2) : ""}</div>
                    <div>{l.credit ? l.credit.toFixed(2) : ""}</div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>

          <div className="btn-row">
            <button className="btn btn-secondary" type="button" onClick={onClose}>Abbrechen</button>
            <button className="btn btn-primary" type="button" disabled={!canCreate} onClick={createProvisional}
              title={!canCreate ? "Lieferant + Konten + Balance nötig" : ""}>
              {creating ? "Buche…" : "Provisorisch buchen"}
            </button>
          </div>

          {!vendor && (
            <div style={{ marginTop: 10, color: "var(--muted)", fontSize: 13 }}>
              Hinweis: Bitte Lieferant setzen (Vorlage greift dann automatisch).
            </div>
          )}
          {hasItemErrors && (
            <div style={{ marginTop: 10, color: "var(--muted)", fontSize: 13 }}>
              Hinweis: Wenn Netto-Betrag gesetzt ist, muss auch ein Konto gewählt sein.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
